import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiMail, FiPhone, FiCalendar, FiUsers, FiInfo } from 'react-icons/fi';
import { getPublicDerasar } from '../../services/api';
import styles from './LandingLayout.module.css';

const buildAddress = (p) => {
  if (!p) return null;
  const lines = [
    [p.flatNo, p.buildingName].filter(Boolean).join(', '),
    p.addressLine1,
    p.addressLine2,
    [p.city, p.state, p.pincode].filter(Boolean).join(', '),
    p.country && p.country !== 'India' ? p.country : '',
  ].filter(Boolean);
  return lines.length ? lines.join(', ') : null;
};

const DerasarDetailPage = () => {
  const { id } = useParams();
  const [derasar, setDerasar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getPublicDerasar(id)
      .then(res => setDerasar(res.data))
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className={styles.loader}><div className={styles.spinner} /></div>
  );

  if (notFound || !derasar) return (
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <p className={styles.emptyMsg}>Derasar not found or no longer active.</p>
        <Link to="/derasars" className={styles.detailBack} style={{ display: 'inline-flex', marginTop: 16 }}>
          <FiArrowLeft size={14} /> Back to Derasars
        </Link>
      </div>
    </section>
  );

  const address = buildAddress(derasar.adminProfile) || [derasar.city, derasar.state].filter(Boolean).join(', ');

  return (
    <>
      {/* Hero */}
      <div className={styles.detailHero}>
        <div className={styles.detailHeroInner}>
          <Link to="/derasars" className={styles.detailBack}>
            <FiArrowLeft size={14} /> All Derasars
          </Link>
          <div className={styles.detailAvatar}>{derasar.name.charAt(0).toUpperCase()}</div>
          <h1 className={styles.detailTitle}>{derasar.name}</h1>
          {derasar.description && <p className={styles.detailDesc}>{derasar.description}</p>}
        </div>
      </div>

      {/* Body */}
      <div className={styles.detailBody}>
        <div className={styles.detailBodyInner}>

          {/* Main column */}
          <div>
            <div className={styles.detailInfoGrid}>
              {address && (
                <div className={styles.detailInfoCard}>
                  <div className={styles.detailInfoLabel}><FiMapPin size={11} style={{ marginRight: 4 }} />Address</div>
                  <div className={styles.detailInfoValue}>{address}</div>
                </div>
              )}
              {derasar.contactEmail && (
                <div className={styles.detailInfoCard}>
                  <div className={styles.detailInfoLabel}><FiMail size={11} style={{ marginRight: 4 }} />Email</div>
                  <div className={styles.detailInfoValue}>
                    <a href={`mailto:${derasar.contactEmail}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
                      {derasar.contactEmail}
                    </a>
                  </div>
                </div>
              )}
              {derasar.contactPhone && (
                <div className={styles.detailInfoCard}>
                  <div className={styles.detailInfoLabel}><FiPhone size={11} style={{ marginRight: 4 }} />Phone</div>
                  <div className={styles.detailInfoValue}>{derasar.contactPhone}</div>
                </div>
              )}
              {derasar.establishDate && (
                <div className={styles.detailInfoCard}>
                  <div className={styles.detailInfoLabel}><FiCalendar size={11} style={{ marginRight: 4 }} />Established</div>
                  <div className={styles.detailInfoValue}>
                    {new Date(derasar.establishDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </div>
              )}
            </div>

            {derasar.notes && (
              <div className={styles.detailNotes}>
                <h3><FiInfo size={14} style={{ marginRight: 6 }} />About this Derasar</h3>
                <p>{derasar.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className={styles.detailSidebar}>
            <h3>Quick Info</h3>
            <div className={styles.sidebarStat}>
              <div className={styles.sidebarStatIcon}><FiUsers size={16} /></div>
              <div>
                <div className={styles.sidebarStatLabel}>Total Persons</div>
                <div className={styles.sidebarStatValue}>{derasar.personCount || '—'}</div>
              </div>
            </div>
            <div className={styles.sidebarStat}>
              <div className={styles.sidebarStatIcon}><FiCalendar size={16} /></div>
              <div>
                <div className={styles.sidebarStatLabel}>Established</div>
                <div className={styles.sidebarStatValue}>
                  {derasar.establishDate ? new Date(derasar.establishDate).getFullYear() : '—'}
                </div>
              </div>
            </div>
            <div className={styles.sidebarStat}>
              <div className={styles.sidebarStatIcon}><FiMapPin size={16} /></div>
              <div>
                <div className={styles.sidebarStatLabel}>Address</div>
                <div className={styles.sidebarStatValue} style={{ fontSize: '0.8rem' }}>{address || '—'}</div>
              </div>
            </div>
            <div className={styles.sidebarStat}>
              <div className={styles.sidebarStatIcon}><FiMail size={16} /></div>
              <div>
                <div className={styles.sidebarStatLabel}>Contact Email</div>
                <div className={styles.sidebarStatValue} style={{ fontSize: '0.8rem' }}>{derasar.contactEmail || '—'}</div>
              </div>
            </div>
            <div className={styles.sidebarStat}>
              <div className={styles.sidebarStatIcon}><FiPhone size={16} /></div>
              <div>
                <div className={styles.sidebarStatLabel}>Phone</div>
                <div className={styles.sidebarStatValue}>{derasar.contactPhone || '—'}</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default DerasarDetailPage;
